import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { Badge } from '../../ui/badge'
import { FileText, FileIcon } from 'lucide-react'
import { EncryptedImageViewer } from './EncryptedImageViewer'

interface VaultDocument {
  vault_id: string
  document_id: string
  document_title: string
  document_filename: string
  document_mime_type: string
  document_signature: string
}

interface KycVaultDocumentsProps {
  documents: VaultDocument[]
}

export const KycVaultDocuments: React.FC<KycVaultDocumentsProps> = ({ documents }) => {
  if (!documents || documents.length === 0) {
    return null
  }

  // Check if documents are images
  const isImageMimeType = (mimeType: string): boolean => {
    return mimeType.startsWith('image/')
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center space-x-2 text-base">
          <FileText className="h-4 w-4" />
          <span>Vault Documents</span>
          <Badge variant="secondary" className="text-xs">{documents.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {documents.map((document, index) => (
            <div key={index} className="space-y-2">
              {isImageMimeType(document.document_mime_type) ? (
                <EncryptedImageViewer
                  documentId={document.document_id}
                  label={document.document_title}
                  mimeType={document.document_mime_type}
                />
              ) : (
                <Card className="border-dashed">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <FileIcon className="h-10 w-10 text-muted-foreground" />
                      <Badge variant="outline" className="text-xs">
                        {document.document_mime_type.split('/').pop()?.toUpperCase() || 'FILE'}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm font-medium">{document.document_title}</p>
                      <p className="text-xs text-muted-foreground font-mono truncate">
                        {document.document_filename}
                      </p>
                    </div>
                    <div className="pt-2 border-t text-xs text-muted-foreground space-y-1">
                      <div>
                        <span className="font-medium">Vault ID:</span>
                        <p className="font-mono text-xs break-all">{document.vault_id}</p>
                      </div>
                      <div>
                        <span className="font-medium">Document ID:</span>
                        <p className="font-mono text-xs break-all">{document.document_id}</p>
                      </div>
                      <div>
                        <span className="font-medium">Signature:</span>
                        <p className="font-mono text-xs break-all">{document.document_signature}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
